/**
 * Address search on Google Places.
 *
 * Google projects created from March 2025 on can no longer enable the legacy Places API, so the search uses
 * Places API (New) first (AutocompleteSuggestion + Place.fetchFields). Older projects that only have the legacy
 * API enabled keep working through the legacy AutocompleteService / PlacesService fallback.
 *
 * The @types/googlemaps version pinned in the project predates Places API (New), hence the `any` casts below.
 */

import { parseCoordinates } from "./coordinates";

export { parseCoordinates };

/** One suggestion shown in the search list */
export interface PlaceSearchSuggestion {
  id: string;
  label: string;
  source: "new" | "legacy";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PlacePrediction (new) or AutocompletePrediction (legacy)
  raw: any;
}

/** Place chosen in the search */
export interface PlaceSearchResult {
  location: google.maps.LatLngLiteral;
  formattedAddress: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the pinned typings lack importLibrary and Places API (New)
const mapsApi = (): any => (window as any).google?.maps;

/** Places library, loading it when the page has not loaded it yet */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see mapsApi
const placesLibrary = async (): Promise<any> => {
  const maps = mapsApi();

  if (maps?.places?.AutocompleteSuggestion) return maps.places;
  if (typeof maps?.importLibrary === "function") return maps.importLibrary("places");

  return maps?.places;
};

// Once the new API failed and the legacy one answered, keep using the legacy one for the rest of the page session
let preferLegacy = false;

/** Reset the API preference (tests only) */
export const resetPlaceSearchPreference = (): void => { preferLegacy = false; };

/**
 * One search session: the suggestions typed until a place is chosen are billed together with its details
 */
export class PlaceSearchSession {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AutocompleteSessionToken of either API
  private newToken: any = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
  private legacyToken: any = null;

  /**
   * Suggestions for the typed text, biased to the map area when given
   * @param input Typed text
   * @param bounds Current map bounds, to rank nearby places first
   */
  async fetchSuggestions(input: string, bounds?: google.maps.LatLngBounds | null): Promise<PlaceSearchSuggestion[]> {

    if (!preferLegacy) {
      try {
        return await this.fetchNewSuggestions(input, bounds);
      } catch (error) {
        console.log("Places API (New) unavailable, trying the legacy Places API", error);
      }
    }

    const suggestions = await this.fetchLegacySuggestions(input, bounds);

    preferLegacy = true;

    return suggestions;
  }

  /**
   * Details (address and location) of the chosen suggestion. Ends the session.
   * @param suggestion Chosen suggestion
   */
  async fetchPlace(suggestion: PlaceSearchSuggestion): Promise<PlaceSearchResult | null> {

    try {
      if (suggestion.source === "legacy") return await this.fetchLegacyPlace(suggestion);

      try {
        return await this.fetchNewPlace(suggestion);
      } catch (error) {
        // Place IDs are shared by both APIs: the legacy details still work where only it is enabled
        console.log("Places API (New) details unavailable, trying the legacy Places API", error);

        return await this.fetchLegacyPlace(suggestion);
      }
    } finally {
      this.newToken = null;
      this.legacyToken = null;
    }
  }

  private async fetchNewSuggestions(input: string, bounds?: google.maps.LatLngBounds | null): Promise<PlaceSearchSuggestion[]> {

    const places = await placesLibrary();

    if (!places?.AutocompleteSuggestion) throw new Error("Places API (New) is not available in this Maps version");

    if (!this.newToken) this.newToken = new places.AutocompleteSessionToken();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AutocompleteRequest
    const request: any = { input, sessionToken: this.newToken };

    if (bounds) request.locationBias = bounds;

    const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    return (suggestions ?? [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AutocompleteSuggestion
      .filter((suggestion: any) => suggestion?.placePrediction)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AutocompleteSuggestion
      .map((suggestion: any) => ({
        id: suggestion.placePrediction.placeId,
        label: suggestion.placePrediction.text?.text ?? String(suggestion.placePrediction.text ?? ""),
        source: "new" as const,
        raw: suggestion.placePrediction
      }));
  }

  private fetchLegacySuggestions(input: string, bounds?: google.maps.LatLngBounds | null): Promise<PlaceSearchSuggestion[]> {

    const places = mapsApi()?.places;

    if (!places?.AutocompleteService) return Promise.reject(new Error("Legacy Places API is not loaded"));

    if (!this.legacyToken) this.legacyToken = new places.AutocompleteSessionToken();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AutocompletionRequest
    const request: any = { input, sessionToken: this.legacyToken };

    if (bounds) request.bounds = bounds;

    return new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AutocompletePrediction[] / PlacesServiceStatus
      new places.AutocompleteService().getPlacePredictions(request, (predictions: any[] | null, status: string) => {

        if (status === places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);

          return;
        }
        if (status !== places.PlacesServiceStatus.OK) {
          reject(new Error(`Legacy Places API: ${status}`));

          return;
        }

        resolve((predictions ?? []).map((prediction) => ({
          id: prediction.place_id,
          label: prediction.description,
          source: "legacy" as const,
          raw: prediction
        })));
      });
    });
  }

  private async fetchNewPlace(suggestion: PlaceSearchSuggestion): Promise<PlaceSearchResult | null> {

    const place = suggestion.raw.toPlace();

    // Only Essentials fields: address and location are all the screens use
    await place.fetchFields({ fields: ["formattedAddress", "location"] });

    if (!place.location) return null;

    return {
      location: { lat: place.location.lat(), lng: place.location.lng() },
      formattedAddress: place.formattedAddress ?? suggestion.label
    };
  }

  private fetchLegacyPlace(suggestion: PlaceSearchSuggestion): Promise<PlaceSearchResult | null> {

    const places = mapsApi()?.places;

    if (!places?.PlacesService) return Promise.reject(new Error("Legacy Places API is not loaded"));

    const service = new places.PlacesService(document.createElement("div"));

    return new Promise((resolve, reject) => {
      service.getDetails(
        { placeId: suggestion.id, fields: ["formatted_address", "geometry"], sessionToken: this.legacyToken },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PlaceResult / PlacesServiceStatus
        (place: any, status: string) => {

          if (status !== places.PlacesServiceStatus.OK || !place?.geometry?.location) {
            if (status === places.PlacesServiceStatus.OK || status === places.PlacesServiceStatus.ZERO_RESULTS) resolve(null);
            else reject(new Error(`Legacy Places API: ${status}`));

            return;
          }

          resolve({
            location: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() },
            formattedAddress: place.formatted_address ?? suggestion.label
          });
        }
      );
    });
  }

}
